import { range } from "lodash"
import { Align } from "../../../common/TextUtils"
import { Fireworks } from "../../generic-sprites/Fireworks"
import { Rectangle } from "../../generic-sprites/Shape"
import { AudioSync } from "../../utils/AudioSync"
import { colorNum } from "../../utils/ColorUtils"
import { PhaserText } from "../../utils/PhaserText"
import { Scale } from "../../utils/Scale"
import { Sprites } from "../../utils/Sprites"
import { CampCravingsAssets } from "./CampCravingsAssets"
import { CampCravingsState } from "./CampCravingsState"
import { CampPos } from "./CampPos"
import { DeckSprite } from "./sprites/DeckSprite"
import { PawnAnim, PlayerPawn } from "./sprites/PlayerPawn"

interface VapeAction {
    delay: number,
    x: number,
    y: number,
    vapeOnly?: boolean
}

/**
 * PostAnim and onAudioDone are mutually exclusive.
 */
class PlayerTurn {
    audio: string
    preAnim: PawnAnim
    postAnim?: PawnAnim
    animAudio?: Phaser.Sound | string
    onAudioDone?: (turnOver: () => void) => void
    vape?: VapeAction
    onPreAnim?: () => void
    lastTurn?: boolean
}

const craveNVapeKey = "craveNVape"
const vapeOnlyKey = "vapeOnly"
const animationVolume = 0.5

export class Round {

    private turnPanelDisplayDuration = 1500

    private computerTurn: Phaser.Group
    private playerTurn: Phaser.Group
    private yourTurnDing: Phaser.Sound
    private vaper: Phaser.Sprite

    constructor(
        private game: Phaser.Game,
        private playerGoesFirst: boolean,
        private roundOver: () => void,
        private computerPawn: PlayerPawn,
        private playerPawn: PlayerPawn,
        private computerDeck: DeckSprite,
        private playerDeck: DeckSprite,
        private playerTurns: Array<PlayerTurn>,
        private computerTurns: Array<PlayerTurn>
    ) {

        this.yourTurnDing = game.add.sound(CampCravingsAssets.YOUR_TURN)
        this.yourTurnDing.volume = 0.25
        this.vaper = Sprites.add(game, CampCravingsAssets.USER_VAPING)
        this.vaper.animations.add(craveNVapeKey, undefined, 9)
        this.vaper.animations.add(vapeOnlyKey, range(16, 72), 9)
        this.vaper.visible = false

        playerDeck.setOnClick(() => this.takePlayerTurn(true))

        const makePanel = (text: string, width: number) => {
            const group = game.add.group()
            const panel = new Rectangle(
                this.game,
                Scale.width() * (1 - width) / 2,
                Scale.height() * 0.375,
                Scale.width() * width,
                Scale.height() * 0.25,
                {fillColor: colorNum.lightGray, cornerRounding: 20, group}
            )
            const panelText = PhaserText.create(this.game, text, {
                fontPx: 48, type: CampCravingsState.FONT_TYPE, group, align: Align.CENTER
            })
            Sprites.centerSpriteInSprite(panel.invisibleSprite, panelText)
            group.visible = false
            return group
        }

        this.computerTurn = makePanel("Computer's turn\nto pick a card", 0.375)
        this.playerTurn = makePanel("Your turn\nto pick a card", 0.325)
    }

    reset = () => {
        this.computerPawn.sprite.destroy()
        this.playerPawn.sprite.destroy()
        this.computerDeck.deck.destroy()
        this.playerDeck.deck.destroy()
    }

    computerTurnComplete = () => {
        this.yourTurnDing.play()
        this.playerTurn.visible = true
        this.game.time.events.add(this.turnPanelDisplayDuration, () => {
            this.playerTurn.visible = false
            this.playerDeck.enable()
        })
    }

    finishTurn = (player: boolean, lastTurn?: boolean) => {
        return () => {
            if (lastTurn) {
                this.roundOver()
            } else if (player) {
                this.takePlayerTurn(false)
            } else {
                this.computerTurnComplete()
            }
        }
    }

    takePlayerTurn = (player: boolean) => {
        let turns
        let pawn
        let deck
        let cardTurnDelay

        if (player) {
            turns = this.playerTurns
            pawn = this.playerPawn
            deck = this.playerDeck
            cardTurnDelay = 0
        } else {
            turns = this.computerTurns
            pawn = this.computerPawn
            deck = this.computerDeck
            cardTurnDelay = this.turnPanelDisplayDuration
            this.computerTurn.visible = true
        }

        const {audio, preAnim, postAnim, onAudioDone, animAudio, vape, onPreAnim, lastTurn} = turns.shift()
        const turnOver = this.finishTurn(player, lastTurn)
        this.playerDeck.disable()
        const playerAudio = this.game.add.audio(audio)
        const audioSync = new AudioSync(this.game, playerAudio)

        this.game.time.events.add(cardTurnDelay, () => {
            this.computerTurn.visible = false

            if (vape) {
                const {delay, x, y, vapeOnly} = vape
                audioSync.add(delay, () => {
                    this.vaper.visible = true
                    this.vaper.x = x - Scale.width() * 0.003
                    this.vaper.y = y - Scale.height() * 0.022
                    pawn.sprite.visible = false
                    this.vaper.animations.play(vapeOnly ? vapeOnlyKey : craveNVapeKey)
                    this.vaper.animations.currentAnim.onComplete.addOnce(() => {
                        this.vaper.visible = false
                        pawn.sprite.visible = true
                    })
                })
            }

            if (onPreAnim) {
                onPreAnim()
            }
            pawn.playAnim(preAnim, () =>
                deck.turnCard(() => audioSync.start())
            )
        })

        audioSync.onStop(() => {
            if (onAudioDone) {
                onAudioDone(turnOver)
            }
            if (postAnim) {
                let postAnimAudio
                if (animAudio) {
                    postAnimAudio = typeof animAudio === "string" ? this.game.add.audio(animAudio, animationVolume) : animAudio
                    postAnimAudio.play()
                }
                pawn.playAnim(postAnim, () => {
                    if (postAnimAudio) {
                        postAnimAudio.stop()
                    }
                    turnOver()
                })

            } else if (!onAudioDone) {
                // If onAudioDone + PostAnim postanim will end turn. Otherwise onAudioDone should handle turn over.
                turnOver()
            }
        })
    }

    start =
        () => {
            if (this.playerGoesFirst) {
                this.computerTurnComplete()
            } else {
                this.takePlayerTurn(false)
            }
        }
}

export const makeRoundOne = (game: Phaser.Game, lake: Phaser.Sprite, roundOver: () => void) => {
    const deck1 = new DeckSprite(game, true, 5, CampCravingsAssets.NON_USER_CARDS_ROUND_1)
    return new Round(
        game,
        false,
        roundOver,
        PlayerPawn.round1NonUserPawn(game),
        PlayerPawn.round1UserPawn(game),
        deck1,
        new DeckSprite(game, false, 4, CampCravingsAssets.USER_CARDS_ROUND_1, deck1.deckLocations),
        [
            {
                audio: CampCravingsAssets.ROUND_1_PLAYER_2_BUS,
                preAnim: PawnAnim.TO_BUS,
                vape: {delay: 2000, x: Scale.width() * 0.121, y: Scale.height() * 0.763, vapeOnly: true}
            },
            {
                audio: CampCravingsAssets.ROUND_1_PLAYER_2_LAKE,
                preAnim: PawnAnim.TO_LAKE,
                postAnim: PawnAnim.TO_POST_LAKE,
                vape: {delay: 2100, x: Scale.width() * 0.156, y: Scale.height() * 0.59}
            },
            {
                audio: CampCravingsAssets.ROUND_1_PLAYER_2_NATURE_TRAIL,
                preAnim: PawnAnim.TO_PRE_NATURE_TRAIL,
                postAnim: PawnAnim.TO_POST_NATURE_TRAIL
            },
            {
                audio: CampCravingsAssets.ROUND_1_PLAYER_2_HORSES,
                preAnim: PawnAnim.TO_PRE_HORSES,
                postAnim: PawnAnim.TO_POST_HORSES,
                animAudio: CampCravingsAssets.HORSE_SLOW,
                vape: {delay: 3300, x: Scale.width() * 0.75, y: Scale.height() * 0.5}
            },
        ],
        [
            {
                audio: CampCravingsAssets.ROUND_1_PLAYER_1_BUS,
                preAnim: PawnAnim.TO_BUS
            },
            {
                audio: CampCravingsAssets.ROUND_1_PLAYER_1_LAKE,
                preAnim: PawnAnim.TO_LAKE,
                postAnim: PawnAnim.TO_POST_LAKE,
                onAudioDone: () => lake.frame = 1
            },
            {
                audio: CampCravingsAssets.ROUND_1_PLAYER_1_NATURE_TRAIL,
                preAnim: PawnAnim.TO_PRE_NATURE_TRAIL,
                postAnim: PawnAnim.TO_POST_NATURE_TRAIL,
                animAudio: game.add.audio(CampCravingsAssets.BIRD_SCREECH, 0.2)
            },
            {
                audio: CampCravingsAssets.ROUND_1_PLAYER_1_HORSES,
                preAnim: PawnAnim.TO_PRE_HORSES,
                postAnim: PawnAnim.TO_POST_HORSES,
                animAudio: CampCravingsAssets.HORSE_FAST
            },
            {
                audio: CampCravingsAssets.ROUND_1_PLAYER_1_FINISH,
                preAnim: PawnAnim.TO_FINISH,
                onAudioDone: (turnOver) => {
                    Fireworks.play(game, CampPos.fireworksX(), CampPos.fireworksY(), turnOver)
                },
                lastTurn: true
            },
        ]
    )
}

export const makeRoundTwo = (game: Phaser.Game, picnic: Phaser.Sprite, lodge: Phaser.Sprite, roundOver: () => void) => {
    const playerPawn = PlayerPawn.round2NonUserPawn(game)
    const computerPawn = PlayerPawn.round2UserPawn(game)
    const nonUserDeck = new DeckSprite(game, true, 5, CampCravingsAssets.NON_USER_CARDS_ROUND_2)
    const fishingAudio = game.add.audio(CampCravingsAssets.FISHING_REEL, 0.25)
    const playFishingAudio = (pawn: PlayerPawn) => {
        return (turnOver: () => void) => {
            const timer = game.time.create()
            timer.add(3100, () => fishingAudio.play())
            timer.start()
            pawn.playAnim(PawnAnim.TO_POST_LAKE, turnOver)
        }
    }
    return new Round(
        game,
        true,
        roundOver,
        computerPawn,
        playerPawn,
        new DeckSprite(game, false, 4, CampCravingsAssets.USER_CARDS_ROUND_2, nonUserDeck.deckLocations),
        nonUserDeck,
        [
            {
                audio: CampCravingsAssets.ROUND_2_PLAYER_1_LAKE,
                preAnim: PawnAnim.TO_LAKE,
                onAudioDone: playFishingAudio(playerPawn)
            },
            {
                audio: CampCravingsAssets.ROUND_2_PLAYER_1_PICNIC,
                preAnim: PawnAnim.TO_PICNIC,
                onAudioDone: (turnOver) => {
                    playerPawn.sprite.visible = false
                    const audio = game.add.audio(CampCravingsAssets.CHILDREN_TALKING, animationVolume)
                    audio.play(undefined, 1000, undefined, true)
                    picnic.animations.play(CampCravingsState.PICNIC_1_ANIM)
                    picnic.animations.currentAnim.onComplete.addOnce(() => {
                        turnOver()
                        audio.stop()
                    })
                }
            },
            {
                audio: CampCravingsAssets.ROUND_2_PLAYER_1_HORSES,
                preAnim: PawnAnim.TO_PRE_HORSES,
                postAnim: PawnAnim.TO_POST_HORSES,
                animAudio: CampCravingsAssets.HORSE_FAST,
                onPreAnim: () => {
                    picnic.frame = 0
                    playerPawn.sprite.visible = true
                    computerPawn.sprite.visible = true
                }
            },
            {
                audio: CampCravingsAssets.ROUND_2_PLAYER_1_LODGE,
                preAnim: PawnAnim.TO_LODGE,
                onAudioDone: (turnOver) => {
                    playerPawn.sprite.visible = false
                    const lodgePartyMusic = game.add.audio(CampCravingsAssets.LODGE_PARTY_MUSIC, animationVolume)
                    lodgePartyMusic.play()
                    lodge.animations.play(CampCravingsState.LODGE_ANIM, 2, true)
                    lodgePartyMusic.onStop.addOnce(() => {
                        playerPawn.sprite.visible = true
                        lodge.animations.stop()
                        lodge.frame = 3
                        turnOver()
                    })
                }
            },
            {
                audio: CampCravingsAssets.ROUND_2_PLAYER_1_FINISH,
                preAnim: PawnAnim.TO_FINISH,
                onAudioDone: (turnOver) => {
                    Fireworks.play(game, CampPos.fireworksX(), CampPos.fireworksY() , turnOver)
                },
                onPreAnim: () => {
                    lodge.frame = 0
                    playerPawn.sprite.visible = true
                },
                lastTurn: true
            },
        ],
        [
            {
                audio: CampCravingsAssets.ROUND_2_PLAYER_2_LAKE,
                preAnim: PawnAnim.TO_LAKE,
                vape: {delay: 4000, x: Scale.width() * 0.156, y: Scale.height() * 0.59},
                onAudioDone: playFishingAudio(computerPawn)
            },
            {
                audio: CampCravingsAssets.ROUND_2_PLAYER_2_PICNIC,
                preAnim: PawnAnim.TO_PICNIC,
                onAudioDone: (turnOver) => {
                    computerPawn.sprite.visible = false
                    picnic.animations.play(CampCravingsState.PICNIC_2_ANIM)
                    picnic.animations.currentAnim.onComplete.addOnce(() => turnOver())
                }
            },
            {
                audio: CampCravingsAssets.ROUND_2_PLAYER_2_HORSES,
                preAnim: PawnAnim.TO_PRE_HORSES,
                postAnim: PawnAnim.TO_POST_HORSES,
                animAudio: CampCravingsAssets.HORSE_SLOW,
                vape: {delay: 3300, x: Scale.width() * 0.75, y: Scale.height() * 0.5}
            },
            {
                audio: CampCravingsAssets.ROUND_2_PLAYER_2_LODGE,
                preAnim: PawnAnim.TO_LODGE,
                vape: {delay: 3000, x: Scale.width() * 0.869, y: Scale.height() * 0.231, vapeOnly: true}
            },
        ]
    )
}
